import {
  Article,
  ImageObject,
  SoftwareApplication,
  VideoObject,
  WebPage,
} from 'schema-dts';
import { findLocalCode, findLocalSlug, siteUrl } from '..';
import { LocaleAllow, MetaProps, SchemaAllow } from '../../../../types/IHeader';
import { getCurrencyObject } from '../../../utils/utils';
import { addTypeElement, overideSchema } from '../functions/parseSchema';
import { aboutUs } from './aboutUs';
import { article } from './article';
import { breadcrumb } from './breadcrumb';
import { collectionPage } from './collectionPage';
import { faq } from './faq';
import { payfitProduct } from './payfitProduct';
import { person } from './person';
import { primaryImage } from './primaryImage';
import { video } from './video';
import { webPage } from './webPage';

import {
  BlogHomeRecord,
  PricingPageV3Record,
  QuestionAnswerRecord,
  SiteLocale,
  TablePriceBlockRecord,
} from '../../../../types/dato';
import { PostMeta } from '../../utils';
import { tablePrices } from './tablePrices';

const isPayfitProductPage = (data: any): data is PricingPageV3Record => {
  return 'pricing' in data;
};
interface FactorySchemaProps {
  data: MetaProps;
  locale: LocaleAllow;
  uri: string;
  allSchema: SchemaAllow[];
  dataWithBreadcrumb?: BlogHomeRecord | null;
  localeSlug: string;
  localeCode: string;
  currencyObject: { code: string; symbol: string };
  schemaArray: SchemaAllow[];
  addWebPage: any;
  addArticle: any;
  addCollectionPage: any;
  addPerson: any;
  addPrimaryImage: any;
  addbreadcrumb: any;
  addVideo: any;
  addAboutUs: any;
  addPayfitProduct: any;
  addFAQ: any;
  getSchema: any;
  addSoftwareApplication: any;
  addTablePrices: any;
  postMeta: PostMeta;
}

export default function FactorySchema(this: { CreateElement: any }) {
  this.CreateElement = function (
    data: MetaProps,
    locale: LocaleAllow,
    uri: string,
    allSchema: SchemaAllow[],
    dataWithBreadcrumb: BlogHomeRecord,
    postMeta: PostMeta,
  ): FactorySchemaProps {
    const _allSchema = allSchema?.filter(
      (item) => item?.['@type'] !== 'SoftwareApplication',
    );

    const schema: FactorySchemaProps = {
      data,
      locale,
      uri,
      allSchema: _allSchema,
      dataWithBreadcrumb,
      localeSlug: findLocalSlug(locale),
      localeCode: findLocalCode(locale),
      currencyObject: getCurrencyObject(locale),
      schemaArray: [{ '@type': 'WebSite' }, { '@type': 'Organization' }],
      postMeta,

      addWebPage: function () {
        let dataWebPage = webPage(
          schema.data,
          schema.localeCode,
          schema.localeSlug,
          schema.uri,
        );
        schema.schemaArray.push(dataWebPage);
        dataWebPage = schema.addVideo(dataWebPage);
      },
      addArticle: function () {
        let dataArticle = article(schema.data, schema.localeSlug, schema.uri);
        dataArticle = schema.addVideo(dataArticle);
        dataArticle = schema.addPerson(dataArticle);
        schema.schemaArray.push(dataArticle);
      },
      addCollectionPage: function () {
        schema.schemaArray.push(
          collectionPage(schema.data, schema.uri, schema.localeSlug),
        );
      },
      addFAQ: function (data: QuestionAnswerRecord[], id: string) {
        const allFaq = faq(data, schema.uri, schema.localeCode, id);
        if (allFaq.length > 0) {
          const findPrevFaqPage = schema.schemaArray.find(
            (item: { '@type': string | string[] }) =>
              item?.['@type']?.includes('FAQPage'),
          );

          const prevMainEntities = findPrevFaqPage
            ? findPrevFaqPage.mainEntity
            : [];
          // first construct all mainEntity
          const mainEntity = allFaq?.reduce(
            function (newMainEntities, item) {
              schema.schemaArray.push(item);
              newMainEntities.push({ '@id': item['@id'] });
              return newMainEntities;
            },
            [...prevMainEntities],
          );

          // Update the object with WebPage
          const newSchemaArray = schema.schemaArray.map(
            (item: { '@type': string | string[] }) => {
              let newItem = item;
              if (typeof item === 'object') {
                if (
                  item?.['@type'] === 'WebPage' ||
                  item?.['@type']?.includes('WebPage')
                ) {
                  newItem = addTypeElement(item, ['FAQPage']);
                  Object.assign(newItem, {
                    mainEntity,
                  });
                }
              }
              return newItem;
            },
          );
          schema.schemaArray = newSchemaArray;
        }
      },

      addPerson: function (data: Article | WebPage) {
        const newData = data;
        if (schema.data?.hero?.[0].author) {
          const author = person(
            schema.data,
            schema.localeCode,
            schema.localeSlug,
          );
          if (typeof author === 'object') {
            schema.schemaArray.push(author);

            const linkAuthor = {
              name: 'name' in author ? author.name : 'Payfit',
              '@id':
                '@id' in author
                  ? author['@id']
                  : `${siteUrl}/${this.localeSlug}#organization`,
            };
            Object.assign(data, { author: linkAuthor });
          }
        }
        return newData;
      },
      addPrimaryImage: function () {
        const newImage: ImageObject | null = primaryImage(
          schema.data,
          schema.uri,
          schema.postMeta,
        );
        if (newImage) {
          schema.schemaArray.push(newImage);
        }
      },
      addPayfitProduct: function () {
        if (isPayfitProductPage(schema.data)) {
          const newProduct = payfitProduct(
            schema.data,
            schema.uri,
            schema.localeSlug,
            schema.currencyObject,
          );

          if (newProduct) {
            schema.schemaArray.push(newProduct);
          }
        }
      },
      addbreadcrumb: function () {
        schema.schemaArray.push(
          breadcrumb(
            schema.data,
            schema.uri,
            schema.locale,
            schema.localeSlug,
            schema.dataWithBreadcrumb,
          ),
        );
      },
      addVideo: function (
        data: SchemaAllow,
        container?: string,
        index?: number,
      ) {
        const heroData = schema.data?.hero?.[0];
        let videoSchema: VideoObject | null;
        if (
          (heroData && ('videoLink' in heroData || 'vimeoLink' in heroData)) ||
          container === 'trackingVideo'
        ) {
          let verifIfVideoSchemaExist = false;
          schema.schemaArray.forEach((item) => {
            if (
              item['@type'] === 'WebPage' ||
              item['@type']?.includes('WebPage')
            ) {
              verifIfVideoSchemaExist = !!item.video;
            }
          });
          if (!verifIfVideoSchemaExist) {
            videoSchema = video(
              schema.data,
              schema.uri,
              schema.localeCode,
              index,
              schema.postMeta,
            );
            if (videoSchema !== null) {
              schema.schemaArray.push(videoSchema);
              schema.schemaArray = schema.schemaArray.map((item) => {
                if (
                  (item['@type'] === 'WebPage' ||
                    item['@type']?.includes('WebPage')) &&
                  !verifIfVideoSchemaExist
                ) {
                  const linkVideo = { '@id': videoSchema?.['@id'] };
                  item.video = linkVideo;
                }
                return item;
              });
            }
          }
        }
        return data;
      },
      addAboutUs: function () {
        const newSchemaArray = schema.schemaArray.map((item) => {
          if (typeof item === 'object') {
            if (item['@type'] === 'WebPage') {
              return aboutUs(item);
            }
          }
          return item;
        });
        schema.schemaArray = newSchemaArray;
      },
      addSoftwareApplication: function (
        jsonSoftwarApplication: SoftwareApplication,
      ) {
        schema.schemaArray.push(jsonSoftwarApplication);
      },
      addTablePrices: function (data: TablePriceBlockRecord) {
        const offers = tablePrices(
          data,
          schema.uri,
          schema.localeSlug as SiteLocale,
          schema.currencyObject,
        );
        schema.schemaArray.push(offers);
      },
      getSchema: function () {
        return schema.schemaArray.map(
          (item: { '@type': string | string[] }) => {
            const schemaFind = schema.allSchema.find((elt) => {
              if (
                elt &&
                typeof elt === 'object' &&
                item &&
                typeof item === 'object'
              ) {
                if ('@type' in elt && '@type' in item) {
                  return elt['@type'] === item['@type'];
                }
              }
              return false;
            });
            if (schemaFind && typeof schemaFind === 'object') {
              overideSchema(schemaFind, item || null);
              return schemaFind;
            }
            return item;
          },
        );
      },
    };

    schema.addWebPage();
    schema.addbreadcrumb();
    schema.addPrimaryImage();

    return schema;
  };
}
